import React, {Component, Fragment} from 'react';
import {observer} from 'mobx-react';
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';

import IconBox from 'lib/struct/icon/IconBox';
import Icon from 'lib/struct/icon/Icon';
import PseudoLinkBox, {PseudoLinkBoxInner} from 'lib/struct/pseudo_link/PseudoLinkBox';
import IconCheckbox from 'lib/struct/form/IconCheckbox';
import Select from 'lib/struct/form/Select';
import InlineCode from 'lib/struct/inlines/InlineCode';
import HoverHint from 'lib/struct/hover_hint/HoverHint';
import InputText from 'lib/struct/form/InputText';
import Modal from 'lib/struct/modal/Modal';

import GroupSettings from './GroupSettings';

import styles from './GroupsSettingsComponent.less';

const DragHandle = SortableHandle(() => (
	<span className={styles['sortable-container__drag-handle']}>
		<Icon name='sort' />
	</span>
));

@observer class GroupsContainer extends Component {
	render() {
		/** @type {SettingsContext} */
		const context = this.props.context;
		const settings = context.settings;

		if (settings.groups.length === 0) {
			return null;
		}

		return (
			<table className={`table zebra ${styles['groups__table']}`}>
				<thead>
				<tr>
					<th className={styles['groups__sort-column']}/>
					<th className={styles['groups__status-column']}>Статус</th>
					<th className={styles['groups__name-column']}>Название</th>
					<th className={styles['groups__template-column']}>Вид</th>
					<th className={styles['groups__settings-column']}>Настройки</th>
					<th className={styles['groups__helper-column']}>
						<HoverHint hint='Используйте хелпер для вывода конкретной группы'>
							Хелперы групп
						</HoverHint>
					</th>
					<th className={styles['groups__delete-column']}/>
				</tr>
				</thead>
				<tbody>
				{
					settings.groups.map((group, index) => (
						<GroupsSortableElement
							key={group._key}
							index={index}
							group={group}
							context={context}
							onRemoveGroup={() => confirm(`Удалить группу ${group.name}?`) && settings.removeGroup(group)}
							template_options={context.group_template_options}
						/>
					))
				}
				</tbody>
			</table>
		);
	}
}

const GroupsSortableContainer = SortableContainer(GroupsContainer);

@observer class GroupsElement extends Component {
	constructor(props) {
		super(props);

		this.state = {
			show_copy_message: false,
		};
	}

	render() {
		/** @type {Group} */
		const group = this.props.group;
		const {template_options, onRemoveGroup} = this.props;
		const {show_copy_message} = this.state;

		/** @type {SettingsContext} */
		const context = this.props.context;

		const product_helper_code = context.getSpecificProductGroupHelper(group.id);
		const category_helper_code = context.getSpecificCategoryGroupHelper(group.id);

		let p_ref;
		let c_ref;

		return (
			<tr>
				<td className={styles['groups__sort-column']}>
					<DragHandle/>
				</td>
				<td className={styles['groups__status-column']}>
					<IconCheckbox
						checked={group.is_shown}
						onChange={e => group.is_shown = !!e.target.checked}
						true_icon='status-green'
						false_icon='status-red'
					/>
				</td>
				<td className={styles['groups__name-column']}>
					<InputText
						value={group.name}
						onChange={e => group.name = e.target.value}
						className={styles['groups__name-input']}
					/>
				</td>
				<td className={styles['groups__template-column']}>
					<Select
						options={template_options}
						value={group.markup_template_id}
						onChange={e => group.markup_template_id = e.target.value}
					/>
				</td>

				<td className={styles['groups__settings-column']}>
					<PseudoLinkBox
						onClick={() => context.openGroupSettingsPopup(group)}
						className={styles['groups__group-settings-link']}
					>
						<Icon name='settings' /> <PseudoLinkBoxInner dashed>Настройки</PseudoLinkBoxInner>
					</PseudoLinkBox>
				</td>

				<td className={styles['groups__helper-column']}>
					{
						context.is_copy_to_clipboard_available ? (
							<Fragment>
								<input
									type='text'
									value={product_helper_code}
									ref={_ref => p_ref = _ref}
									className={styles['code-helper__input']}
									readOnly
								/>
								<input
									type='text'
									value={category_helper_code}
									ref={_ref => c_ref = _ref}
									className={styles['code-helper__input']}
									readOnly
								/>
								<PseudoLinkBox
									onClick={() => this.putIntoClipboard(p_ref, context)}
									title='Скопировать код в буфер обмена'
								>
									<Icon name='notebooks' /> <PseudoLinkBoxInner dashed>Карточка</PseudoLinkBoxInner>
								</PseudoLinkBox>

								<PseudoLinkBox
									onClick={() => this.putIntoClipboard(c_ref, context)}
									title='Скопировать код в буфер обмена'
									className={styles['code-helper__category-code-button']}
								>
									<Icon name='notebooks' /> <PseudoLinkBoxInner dashed>Каталог</PseudoLinkBoxInner>
								</PseudoLinkBox>
								{show_copy_message && (<div className={styles['code-helper__message']}>Скопировано</div>)}
							</Fragment>
						) : (
							<Fragment>
								<InlineCode>{product_helper_code}</InlineCode>
								<InlineCode>{category_helper_code}</InlineCode>
							</Fragment>
						)
					}
				</td>
				<td className={styles['groups__delete-column']}>
					<PseudoLinkBox onClick={() => onRemoveGroup()}>
						<Icon name='no' />
					</PseudoLinkBox>
				</td>
			</tr>
		);
	}

	putIntoClipboard (input_ref, context) {
		input_ref.focus();
		input_ref.select();

		let success = false;

		try {
			success = document.execCommand('copy');
		} catch (err) {}

		if (success) {
			if (this.copy_message_timeout) {
				clearTimeout(this.copy_message_timeout);
			}

			this.setState({
				show_copy_message: true,
			});

			this.copy_message_timeout = setTimeout(() => {
				this.copy_message_timeout = 0;

				this.setState({
					show_copy_message: false,
				});
			}, 1500);
		} else {
			context.disableCopyToClipboard()
		}
	}
}

const GroupsSortableElement = SortableElement(GroupsElement);

@observer export default class GroupsSettingsComponent extends Component {
	render() {
		/** @type {SettingsContext} */
		const context = this.props.context;
		const settings = context.settings;

		return (
			<div className={styles['block']}>
				<h2 className={styles['groups__header']}>Группы</h2>

				<GroupsSortableContainer
					context={context}
					helperClass={styles['sortable-container__helper']}
					onSortEnd={({oldIndex, newIndex}) => {
						settings.changeGroupsOrder(oldIndex, newIndex);
						$('body').removeClass(styles['sortable-container__body-helper'])
					}}
					onSortStart={() => {
						$('body').addClass(styles['sortable-container__body-helper']);
					}}

					axis='y'
					lockAxis='y'
					useDragHandle
					lockToContainerEdges
				/>

				<PseudoLinkBox
					onClick={() => settings.addNewGroup()}
					className={styles['add-group__block']}
				>
					<IconBox icon={(<Icon name='add' />)}>
						<PseudoLinkBoxInner>Добавить новую группу</PseudoLinkBoxInner>
					</IconBox>
				</PseudoLinkBox>

				<Modal
					isOpen={!!context.edited_group}
					onClose={() => context.closeGroupSettingsPopup()}
				>
					<GroupSettings
						context={context}
					/>
				</Modal>
			</div>
		);
	}
}